import { useEffect, useMemo, useState } from "react";
import { PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { UsuarioAdmin } from "@/lib/types";
import { useAreasAdmin } from "@/features/admin-areas/hooks/use-areas-admin";
import { useOrganizacionesAdmin } from "@/features/admin-organizaciones/hooks/use-organizaciones-admin";
import { useUsuariosAdmin } from "../hooks/use-usuarios-admin";
import { listarUsuariosAdmin } from "../services/usuarios-service";
import { useRolesDisponibles } from "../hooks/use-roles-disponibles";
import { UsuariosHeader } from "./UsuariosHeader";
import { UsuariosFiltros } from "./UsuariosFiltros";
import { UsuariosDesktopTable } from "./UsuariosDesktopTable";
import { UsuariosMobileList } from "./UsuariosMobileList";

const PAGE_SIZE = 15;

/**
 * Página de administración de usuarios · el listado se filtra y pagina en
 * el SERVIDOR (busqueda/rol/organizacionId + page, con debounce en el
 * texto). Áreas y organizaciones completas se cargan aparte (suspense ·
 * precargadas en el loader) porque el panel de edición asigna sobre el
 * catálogo COMPLETO, no sobre la página visible.
 *
 * `editandoId` es state local del container · solo una fila puede
 * estar expandida a la vez para no llenar la pantalla.
 */
export function AdminUsuariosPage() {
  const { data: areas } = useAreasAdmin();
  const { data: organizaciones } = useOrganizacionesAdmin();

  const rolesDisponibles = useRolesDisponibles();
  const [filtro, setFiltro] = useState("");
  const [rol, setRol] = useState("");
  const [org, setOrg] = useState("");
  const [page, setPage] = useState(1);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  const filtrosServer = useMemo(
    () => ({
      ...(filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
      ...(rol ? { rol } : {}),
      ...(org ? { organizacionId: org } : {}),
    }),
    [filtroDebounced, rol, org]
  );

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtroDebounced, rol, org]);

  const { data } = useUsuariosAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const usuarios = data?.items ?? [];
  const total = data?.total ?? 0;

  const opcionesOrg = useMemo(() => organizaciones.map((o) => ({ value: o.id, label: o.nombre })), [organizaciones]);

  const toggleEditar = (id: string) => setEditandoId((prev) => (prev === id ? null : id));

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodos = async (): Promise<UsuarioAdmin[]> => {
    const todos: UsuarioAdmin[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarUsuariosAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <UsuariosHeader total={data?.totalCatalogo ?? 0} />
      <Stack gap="md">
        <UsuariosFiltros
          texto={filtro}
          onTextoChange={setFiltro}
          rol={rol}
          onRolChange={setRol}
          rolesDisponibles={rolesDisponibles}
          org={org}
          onOrgChange={setOrg}
          organizaciones={opcionesOrg}
        />
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <UsuariosDesktopTable
          usuarios={usuarios}
          todasAreas={areas}
          todasOrganizaciones={organizaciones}
          editandoId={editandoId}
          onToggleEditar={toggleEditar}
          obtenerFilas={obtenerTodos}
        />
        <UsuariosMobileList
          usuarios={usuarios}
          todasAreas={areas}
          todasOrganizaciones={organizaciones}
          editandoId={editandoId}
          onToggleEditar={toggleEditar}
        />
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="usuarios" />
      </Stack>
    </div>
  );
}
